/**
 * Type definitions for every data shape the console reads.
 *
 * These mirror the JSON files we plan to publish to R2 in Phase 1
 * (mac-status.json, vps-status.json, anthropic-spend.json) plus the
 * existing artifacts (bets.json, dashboard.json, R2 _latest.json).
 *
 * Phase 0 ships these as the contract; phase 1 wires real producers
 * (mac reporter daemon, vps reporter, cost collector).
 */

// =============================================================
// Mac reporter — from a small LaunchAgent on the operator's Mac
// =============================================================
export interface MacStatus {
  /** ISO 8601 UTC of when this snapshot was written */
  generated_at: string;
  hostname: string;
  /** Disk free GB on root volume */
  disk_free_gb: number;
  /** Memory free GB */
  memory_free_gb: number;
  load_avg: [number, number, number];
  /** All com.yurysankov.* LaunchAgents */
  launchagents: LaunchAgentStatus[];
  /** Recent commits (sports-betting-research + dashboard-site, last 20 each) */
  recent_commits: GitCommit[];
}

export interface LaunchAgentStatus {
  label: string;
  loaded: boolean;
  last_exit_code: number | null;
  /** ISO 8601 UTC of next scheduled fire (StartCalendarInterval) */
  next_fire: string | null;
  /** ISO 8601 UTC of last successful fire (parsed from log mtime) */
  last_fire: string | null;
  /** Path to log file for click-through */
  log_path: string;
  /** Human-readable schedule description ("8:00 AM ET daily") */
  schedule_label: string;
}

export interface GitCommit {
  repo: "sports-betting-research" | "dashboard-site" | "the-kitchen-console";
  sha: string;
  short_sha: string;
  message: string;
  author: string;
  iso_date: string;
  /** Phase tag if present (e.g. "PHASE-13.20", "EV-LIVE-2026-05-09") */
  phase_tag: string | null;
}

// =============================================================
// VPS reporter — from kitchen-vps's run_scrape.sh extension
// =============================================================
export interface VpsStatus {
  generated_at: string;
  hostname: string;
  /** Last successful scrape end time (newest per-event hr-*.json mtime) */
  last_scrape_at: string | null;
  /** Last time the smart-cadence gate fired (proxies "is the systemd
   *  timer + Python gate alive"). Stays fresh even when HR rate-limits
   *  the scrape data — used to distinguish "scraper broken" from
   *  "scraper alive but upstream throttling". */
  last_gate_fire_at?: string | null;
  /** Smart-cadence gate state */
  cadence: "prime" | "off-peak" | "skipped";
  /** Why a skip happened on the most recent tick (or null) */
  skip_reason: string | null;
  disk_free_gb: number;
  memory_free_gb: number;
  /** Per-sport last-scrape ages in seconds */
  sport_freshness: Record<"nba" | "wnba" | "mlb" | "nhl" | "soccer" | "ufc", number>;
  /** Recent error messages, newest first */
  recent_errors: { ts: string; message: string }[];
  /** Total bytes uploaded to R2 in last 24h (capacity check) */
  bytes_uploaded_24h: number;
  /** SCRAPER-CONSOLIDATION-2026-05-12 — 12 buckets × 5 min covering the
   *  last hour, newest-first, each value = count of scrape file writes
   *  in that window. Drives the VPS row sparkline. */
  pulse_history?: number[];
}

// =============================================================
// Cloudflare Workers status — combined catalog-status + catalog-history
// from parlay-scorer (the only worker that owns a 1-min cron right now).
// Surfaces deployed-count, last cron pass, rolling 60-min flow, cost
// projection. Lives on the left rail alongside the VPS scraper panel.
// =============================================================
export interface WorkersStatus {
  /** Total workers we monitor (currently 1: parlay-scorer; console-worker
   *  is the server we're talking to, intentionally not self-counted) */
  deployed: number;
  /** Per-worker breakdown so we can grow this beyond just parlay-scorer */
  workers: WorkerSummary[];
  /** Aggregated cost across all monitored workers ($/month estimate) */
  cost: {
    base_cost_usd:       number;   // $5/mo paid plan
    overage_usd:         number;   // beyond 10M req/mo
    total_estimated_usd: number;
    free_tier_included:  number;   // 10_000_000
    projected_monthly:   number;   // sum of all workers' req/month
  };
  generated_at: string;
  /** True only when the proxy can't reach parlay-scorer */
  unreachable?: boolean;
  error?:       string;
}

export interface WorkerSummary {
  /** Stable identifier ("parlay-scorer") */
  name: string;
  /** Human-readable purpose */
  role: string;
  /** Healthy if the cron fired within the last 5 minutes */
  alive: boolean;
  /** SCRAPER-CONSOLIDATION-2026-05-12 — expected cadence in seconds,
   *  drives the "5m" / "10m" / "30m" pill on each row. */
  cadence_seconds?: number;
  /** Tier label: primary / fallback / standby (disabled). Drives the
   *  small priority pill next to the cadence. */
  priority?: "primary" | "fallback" | "standby";
  last_pass_at:   string | null;   // ISO
  age_seconds:    number;          // since last_pass_at
  duration_ms:    number;
  events_scanned: number;
  events_failed:  number;
  new_selections: number;
  refreshed:      number;
  total_after:    number;          // catalog size
  consecutive_429: number;
  aborted_early:  boolean;
  /** When aborted_early, the human-readable reason —
   *  "rate_limited" / "discover_empty" / "batch_failed" / "budget_exceeded" /
   *  "uncaught:..." etc. */
  abort_reason?:  string | null;
  /** Rolling 60-min totals */
  rolling: {
    passes:          number;
    total_new:       number;
    total_refreshed: number;
    total_failed:    number;
  };
  /** Recent passes (newest first), for the sparkline */
  history: WorkerPass[];
}

export interface WorkerPass {
  started_at:     string;
  finished_at:    string;
  duration_ms:    number;
  events_scanned: number;
  events_failed:  number;
  new_selections: number;
  refreshed:      number;
  total_after:    number;
  aborted_early:  boolean;
}

// =============================================================
// Anthropic spend — daily cron parses Admin API export
// (legacy shape kept for the left-rail spend pillar)
// =============================================================
export interface AnthropicSpendDay {
  date: string; // YYYY-MM-DD
  total_usd: number;
  /** Cost per pipeline stage (best-effort tagging) */
  by_pipeline: Record<string, number>;
  /** Cost per sport */
  by_sport: Record<"NBA" | "WNBA" | "MLB" | "NHL" | "SOCCER" | "UFC" | "OTHER", number>;
  /** Prompt cache hit rate 0-1 over the day */
  cache_hit_rate: number;
  /** Total input tokens, output tokens, request count */
  totals: { input_tokens: number; output_tokens: number; requests: number };
}

// =============================================================
// Cost Console (Phase 3) — live aggregation from worker usage logs
// =============================================================
export interface CostSpendSnapshot {
  today:                 string;          // YYYY-MM-DD
  today_cost_usd:        number;
  avg_7d_usd:            number;
  delta_vs_7d_usd:       number;
  projection_month_usd:  number;
  days_remaining_in_month: number;
  cache_hit_rate:        number | null;
  window_total: {
    days_in_window:               number;
    requests:                     number;
    cost_usd:                     number;
    input_tokens:                 number;
    output_tokens:                number;
    cache_creation_input_tokens:  number;
    cache_read_input_tokens:      number;
  };
  /** Window-rolled per-sport breakdown — keys: NBA / MLB / NHL / SOCCER /
   *  UFC / TENNIS for picker calls; "console" bucket aggregates non-picker
   *  worker spend (explain, ask, morning-brief). */
  window_by_sport?: Record<string, {
    requests:      number;
    cost_usd:      number;
    input_tokens:  number;
    output_tokens: number;
  }>;
  series: CostDailyBucket[];           // chronological, oldest → newest
  generated_at: string;
}
export interface CostDailyBucket {
  date:                          string;
  requests:                      number;
  cost_usd:                      number;
  input_tokens:                  number;
  output_tokens:                 number;
  cache_creation_input_tokens:   number;
  cache_read_input_tokens:       number;
  by_source: Record<string, { requests: number; cost_usd: number }>;
  by_model:  Record<string, { requests: number; cost_usd: number }>;
  by_sport?: Record<string, { requests: number; cost_usd: number;
                                input_tokens: number; output_tokens: number }>;
}

// =============================================================
// Today's slate — abridged view of dashboard.json bets state
// =============================================================
export interface TodaysSlateSummary {
  date: string;
  by_sport: {
    sport: "NBA" | "WNBA" | "MLB" | "NHL" | "NFL" | "SOCCER" | "UFC";
    posted: number;
    graded: number;
    pending: number;
    pnl_units: number | null;
  }[];
  /** Sparkline of total bet count per day for last 7 days */
  count_sparkline: number[];
}

// =============================================================
// Anomaly badges — computed by the console worker
// =============================================================
export interface AnomalyBadges {
  generated_at: string;
  badges: AnomalyBadge[];
}
export interface AnomalyBadge {
  id: string;
  severity: "info" | "warn" | "error";
  title: string;
  detail: string;
  /** Where to click through */
  link?: string;
}

// =============================================================
// Activity ticker — scrolling event feed in the right rail
// =============================================================
export interface ActivityEvent {
  id: string;
  ts: string;
  category: "scrape" | "picker" | "grader" | "guardrail" | "sync" | "deploy" | "outcome" | "alert";
  message: string;
  details?: string;
}

// =============================================================
// Schedule Gantt — center stage default
// =============================================================
export interface ScheduleEntry {
  agent_label: string;
  display_name: string;
  /** Cron / calendar interval description */
  schedule: string;
  /** Concrete fire times for the next 24 hours (UTC ISO) */
  fires_next_24h: string[];
  /** Concrete fire times for the previous 24 hours (UTC ISO) */
  fires_prev_24h: ScheduledFire[];
  category: "scrape" | "picker" | "probables" | "grader" | "sync" | "calibration" | "other";
}
export interface ScheduledFire {
  ts: string;
  duration_ms: number;
  status: "ok" | "warn" | "fail" | "skipped";
  /** Last 50 lines of the run's log */
  log_tail?: string;
}

// =============================================================
// Auth — read from CF Access JWT
// =============================================================
export interface AuthIdentity {
  email: string;
  name: string;
  signed_in_via: "cloudflare-access" | "dev-passthrough";
}

// =============================================================
// Pipeline graph — Cytoscape DAG nodes + edges
// =============================================================
export interface PipelineGraph {
  generated_at: string;
  nodes: PipelineNode[];
  edges: PipelineEdge[];
}

export interface PipelineNode {
  id:          string;
  label:       string;
  kind:        "agent" | "scraper" | "store" | "worker" | "ui" | "external";
  category?:   string;
  status?:     "ok" | "warn" | "error" | "idle";
  detail?:     string;
  last_fire?:  string | null;
  next_fire?:  string | null;
  log_path?:   string | null;
  source_url?: string;
}

export interface PipelineEdge {
  id:      string;
  source:  string;
  target:  string;
  flowing: boolean;
  kind:    "data" | "control" | "deploy";
  label?:  string;
}

// =============================================================
// Calibration heatmap (Phase 2.C)
// =============================================================
export interface CalibrationSnapshot {
  as_of:        string | null;
  window_days:  number | null;
  n_decided:    number;
  wins:         number;
  losses:       number;
  pnl_units:    number;
  health:       "green" | "yellow" | "red" | "unknown" | string;
  sports:       CalibrationSport[];
  buckets:      string[];                  // ["90+", "70-89", "50-69", "<50"]
  heatmap:      CalibrationCell[];
  cohorts:      Record<string, CalibrationCohort>;
  generated_at: string;
}
export interface CalibrationSport {
  id:        string;                       // "all" | "NBA" | "MLB" | ...
  n_decided: number;
  pnl_units: number;
  health:    string;
}
export interface CalibrationCell {
  sport:    string;
  bucket:   string;
  delta_pp: number | null;
  hit_rate: number | null;
  decided:  number;
  hits:     number;
  ok:       boolean | null;
}
export interface CalibrationCohort {
  n_decided: number;
  wins:      number;
  losses:    number;
  pushes:    number;
  pnl_units: number;
  buckets:   Record<string, {
    decided:  number;
    hits:     number;
    hit_rate: number | null;
    delta_pp: number | null;
    target:   { lo: number; hi: number; midpoint: number };
    ok:       boolean | null;
  }>;
  health:    string;
}

export interface CalibrationHistorySnapshot {
  date:         string;     // YYYY-MM-DD
  generated_at: string;
  n_decided:    number;
  pnl_units:    number;
  health:       string;
  per_sport:    Record<string, {
    n_decided: number;
    pnl_units: number;
    health:    string;
    buckets:   Record<string, {
      hit_rate: number | null;
      delta_pp: number | null;
      decided:  number;
    }>;
  }>;
}
export interface CalibrationHistory {
  dates:        string[];
  snapshots:    CalibrationHistorySnapshot[];
  generated_at: string;
  note?:        string;
}

// =============================================================
// Batch 2 — log tails, manual triggers, dashboard snapshots
// =============================================================
export interface LaunchAgentLogTail {
  label:           string;
  bytes:           number;
  total_lines:     number;
  returned_lines:  number;
  log:             string;
  fetched_at:      string;
  error?:          string;
}

export interface LaunchAgentLogIndex {
  generated_at: string;
  count:        number;
  agents: Array<{
    label:      string;
    short:      string;
    log_path:   string;
    size_bytes: number;
    lines:      number;
    log_mtime:  string | null;
  }>;
  note?:        string;
}

export interface DashboardDiffChange {
  path:   string;
  kind:   "added" | "removed" | "changed";
  before?: any;
  after?:  any;
}
export interface DashboardDiff {
  a:               string;
  b:               string;
  changes:         DashboardDiffChange[];
  sport_summary:   Record<string, { added: number; removed: number; changed: number }>;
  total_changes:   number;
  truncated:       boolean;
}

// =============================================================
// Bet Inspector (Phase 2.D)
// =============================================================
export interface BetsResponse {
  total:        number;
  returned:     number;
  bets:         Bet[];
  generated_at: string;
}

/** Bet shape — superset of fields actually populated by the picker
 *  pipeline; many are optional because soccer/UFC use different
 *  subsets. We treat unknowns as "?" in the UI. */
export interface Bet {
  id:                 string;
  sport:              string;
  date?:              string;
  placed_at?:         string;
  strategy_id?:       string;
  player?:            string | null;
  player_id?:         string | null;
  team?:              string;
  opp?:               string;
  matchup?:           string;
  market?:            string;
  line?:              number | string | null;
  side?:              string;
  price?:             number | string | null;
  stake?:             number;
  book?:              string;
  hr_bet_id?:         string | null;
  event_id?:          string;
  expected_close?:    number | null;
  closing_line?:      number | null;
  closing_price?:     number | null;
  result_stat?:       string;
  outcome?:           string | null;
  pnl?:               number | null;
  clv?:               number | null;
  notes?:             string;
  injury_context?:    string;
  llm_confidence?:    number | null;
  llm_reasoning?:     string;
  llm_flags?:         string[];
  llm_tier?:          string;
  llm_scored_at?:     string;
  ai_prompts?:        Record<string, unknown>;
  ai_consensus_count?: number;
  ai_picker_convictions?: Record<string, number | null>;
  ai_picker_thesis?:  string;
  graded_at?:         string;
  implied_prob_pct?:  number | null;
  our_prob_pct?:      number | null;
  edge_pp?:           number | null;
  ev_per_unit?:       number | null;
  // soccer-specific
  league?:            string;
  kickoff_utc?:       string;
  home_team?:         string;
  away_team?:         string;
  home_abbr?:         string;
  away_abbr?:         string;
  lineup_status?:     string;
  lineup_status_ts?:  string;
}

// =============================================================
// Prompt Workbench (Phase 3.C)
// =============================================================
export interface PromptIndex {
  generated_at:   string;
  grader_version: string | null;
  prompts:        PromptDescriptor[];
}
export interface PromptDescriptor {
  kind:    "picker" | "grader";
  sport:   string;
  name:    string;
  path:    string;       // relative to _console/prompts/, e.g. "picker/nba/_core.md"
  bytes:   number;
  lines:   number;
  mtime:   string;
  version?: string | null;  // grader prompts carry PROMPT_VERSION
}

// =============================================================
// Rewind / Snapshots (Phase 5.A)
// =============================================================
export interface SnapshotsIndex {
  sources:          string[];
  counts:           Record<string, number>;
  timestamps:       string[];      // ISO, sorted ascending; both-sources only
  earliest:         string | null;
  latest:           string | null;
  interval_minutes: number;
  generated_at:     string;
}

export interface SnapshotPayload {
  source:        string;
  at:            string;
  resolved_iso:  string;
  payload:       any;              // shape depends on source
}

// =============================================================
// HR Rotation (Phase 4.C)
// =============================================================
export interface HrRotation {
  total_competitions: number;
  sports_seen:        string[];
  daily_series:       HrRotationDay[];
  longevity_buckets:  Record<string, number>;
  recent_rotations:   HrCompetition[];
  bet_id_files:       number;
  generated_at:       string;
}
export interface HrRotationDay {
  date:      string;            // YYYY-MM-DD
  per_sport: Record<string, number>;
  total:     number;
}
export interface HrCompetition {
  id:          string;
  name:        string;
  compName:    string;          // raw HR comp name (e.g. "USA - MLS")
  sport:       string;          // canonical (NBA / MLB / SOCCER / ...)
  captured_at: string | null;
  eventTime:   string | null;
  lead_hours:  number | null;
}

// =============================================================
// Anomaly events (Phase 5.D)
// =============================================================
export interface AnomalyEventLog {
  events:       AnomalyEvent[];
  total:        number;
  generated_at?: string;
  detail?:      string;
}
export interface AnomalyEvent {
  ts:        string;
  kind:      "fired" | "resolved";
  id:        string;
  severity?: "info" | "warn" | "error";
  title?:    string;
  detail?:   string;
}

// =============================================================
// TODOs (Phase 6.C)
// =============================================================
export interface TodosIndex {
  generated_at?: string;
  total:        number;
  by_marker:    Record<string, number>;
  by_repo:      Record<string, number>;
  todos:        TodoItem[];
  error?:       string;
  detail?:      string;
}
export interface TodoItem {
  repo:    string;
  path:    string;
  line:    number;
  marker:  "TODO" | "FIXME" | "XXX" | "HACK";
  text:    string;
  mtime:   string;
}

// =============================================================
// Morning brief (Phase 7)
// =============================================================
export interface MorningBriefLatest {
  date:               string;
  generated_at:       string;
  input_tokens:       number;
  output_tokens:      number;
  cost_usd:           number;
  n_anomalies_24h:    number;
  n_bets_yesterday:   number;
  has_top_miss:       boolean;
  brief?:             string;            // markdown content (full body)
  error?:             string;
  detail?:            string;
}

// =============================================================
// Easter Egg events (Phase 6.B.2)
// =============================================================
export interface EasterEggEventsAgg {
  total:        number;
  counts:       Record<string, Record<string, number>>;   // egg_id → kind → count
  daily_by_egg: Record<string, Record<string, number>>;
  dates:        string[];
  recent:       EasterEggEvent[];
  generated_at: string;
}
export interface EasterEggEvent {
  ts:        string;
  egg_id:    string;
  kind:      string;
  ua?:       string | null;
  viewport?: string | null;
  ip?:       string | null;
}

// =============================================================
// Egg Studio (Phase 6.B.3)
// =============================================================
export interface ActiveEgg {
  id:           string;
  name:         string;
  asset_url:    string;
  expires_at:   string;
  storage_key:  string;
  animation:    "slide-up";
  telemetry_id: string;
  deployed_at:  string;
  deployed_by?: string;
  /** Optional per-egg render tuning. Defaults applied client-side if absent. */
  render?:      EggRenderConfig;
}
export interface EggRenderConfig {
  height_pct?:    number;     // image height as % of phone height (default 60)
  width_pct?:     number;     // image max-width as % of phone width  (default 95)
  x_offset_pct?:  number;     // horizontal offset from center, % of phone width (default 0)
  y_offset_px?:   number;     // vertical offset from bottom, px (default 0)
  anim_ms?:       number;     // slide-up animation duration ms (default 480)
  delay_ms?:      number;     // wait before slide-up starts ms (default 200)
}
export const DEFAULT_RENDER: Required<EggRenderConfig> = {
  height_pct:   60,
  width_pct:    95,
  x_offset_pct: 0,
  y_offset_px:  0,
  anim_ms:      480,
  delay_ms:     200,
};
